/**
 * DynamoDB Repository base class.
 * Provides typed access to the single-table design with optimistic concurrency.
 */

export interface QueryOptions {
  readonly limit?: number;
  readonly cursor?: string;
  readonly scanForward?: boolean;
}

export interface PaginatedResult<T> {
  readonly items: T[];
  readonly nextCursor: string | null;
}

export interface DynamoItem {
  readonly PK: string;
  readonly SK: string;
  readonly entityType: string;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly ttl?: number;
  readonly GSI1PK?: string;
  readonly GSI1SK?: string;
  readonly GSI2PK?: string;
  readonly GSI2SK?: string;
  [key: string]: unknown;
}

/**
 * Error thrown when an optimistic concurrency check fails.
 */
export class ConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConflictError';
  }
}

/**
 * Error thrown when an item is not found.
 */
export class NotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NotFoundError';
  }
}

/**
 * Repository interface for DynamoDB single-table access.
 * Each service implements this with its own DynamoDB client instance.
 */
export interface Repository<T extends DynamoItem> {
  /** Get a single item by primary key */
  get(pk: string, sk: string): Promise<T | null>;

  /** Put (create or overwrite) an item */
  put(item: T): Promise<void>;

  /** Query items by partition key and sort key prefix */
  query(pk: string, skPrefix: string, options?: QueryOptions): Promise<PaginatedResult<T>>;

  /** Query a GSI */
  queryIndex(indexName: string, pk: string, skPrefix: string, options?: QueryOptions): Promise<PaginatedResult<T>>;

  /**
   * Update an item with optimistic concurrency control.
   * Throws ConflictError if the current version doesn't match expectedVersion.
   */
  update(pk: string, sk: string, updates: Partial<T>, expectedVersion: number): Promise<T>;

  /** Delete an item */
  delete(pk: string, sk: string): Promise<void>;

  /**
   * Transactional write — atomically write multiple items.
   * Used for operations like WIP limit enforcement.
   */
  transactWrite(items: TransactItem[]): Promise<void>;
}

export type TransactItem =
  | { type: 'put'; item: DynamoItem }
  | { type: 'update'; pk: string; sk: string; updates: Partial<DynamoItem>; expectedVersion: number }
  | { type: 'delete'; pk: string; sk: string }
  | { type: 'conditionCheck'; pk: string; sk: string; condition: string; expressionValues: Record<string, unknown> };
